import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, Star, CheckCircle2, Quote } from "lucide-react";
import { COMPANY, SERVICES, VALUES, STEPS, TESTIMONIALS, IMAGES } from "../data/site";
import { Reveal, Overline } from "../components/Reveal";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-navy text-white overflow-hidden grain">
        <div className="absolute top-0 right-0 w-[40rem] h-[40rem] bg-brand/20 rounded-full blur-[140px]" />
        <div className="absolute -bottom-40 -left-20 w-[30rem] h-[30rem] bg-brand/10 rounded-full blur-[120px]" />
        <div className="max-w-7xl mx-auto px-5 md:px-10 pt-20 pb-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-2 gap-16 items-center relative">
          <div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <Overline>Maine-et-Loire · Angers · 49</Overline>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.98] mt-5"
            >
              Installer.<br />
              Dépanner.<br />
              <span className="text-brand">Optimiser.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-white/70 text-lg md:text-xl mt-8 max-w-xl leading-relaxed"
            >
              {COMPANY.subtitle}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4 mt-10"
            >
              <Link
                to="/devis"
                data-testid="hero-quote-btn"
                className="group bg-brand hover:bg-brand-dark text-white font-bold py-4 px-8 rounded-full shadow-xl shadow-brand/30 transition-colors flex items-center justify-center gap-2"
              >
                Devis gratuit
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href={`tel:${COMPANY.phoneRaw}`}
                data-testid="hero-call-btn"
                className="border-2 border-white/20 hover:border-brand text-white font-bold py-4 px-8 rounded-full transition-colors flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" /> {COMPANY.phone}
              </a>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-10 text-sm text-white/60"
            >
              {["Devis gratuit", "Intervention rapide", "Travail soigné"].map((t) => (
                <span key={t} className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-brand" /> {t}
                </span>
              ))}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="rounded-3xl overflow-hidden shadow-2xl border border-white/10">
              <img src={IMAGES.hero} alt="Climatisation murale réversible intégrée dans une maison moderne et lumineuse — MS ÉNERGIE, Angers" className="w-full h-[520px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white text-navy rounded-2xl p-5 shadow-xl animate-float">
              <div className="flex items-center gap-1 text-brand">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
              </div>
              <p className="font-display font-extrabold text-2xl mt-1">+ de 500</p>
              <p className="text-navy/60 text-sm font-medium">interventions réalisées</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* VALUES STRIP */}
      <section className="bg-surface border-y border-border">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-10 grid grid-cols-2 md:grid-cols-5 gap-6">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.07} className="flex flex-col items-center text-center gap-2">
              <v.icon className="w-8 h-8 text-brand" />
              <span className="font-display font-bold text-navy text-sm md:text-base">{v.title}</span>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-24 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <Overline>Nos expertises</Overline>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-navy mt-4">
              Des solutions énergétiques<br className="hidden md:block" /> complètes
            </h2>
          </div>
          <Link to="/services" className="text-navy font-bold flex items-center gap-2 hover:text-brand transition-colors">
            Tous nos services <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.08}>
              <div className="group bg-white border border-border rounded-2xl p-7 h-full hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(10,25,47,0.1)] transition-all duration-300" data-testid={`service-card-${s.slug}`}>
                <div className="w-14 h-14 rounded-xl bg-navy flex items-center justify-center group-hover:bg-brand transition-colors">
                  <s.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-display font-bold text-xl text-navy mt-6">{s.title}</h3>
                <p className="text-text-muted text-sm mt-3 leading-relaxed">{s.short}</p>
                <Link to="/services" className="inline-flex items-center gap-1 text-brand font-bold text-sm mt-5 group-hover:gap-2 transition-all">
                  En savoir plus <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SPLIT CTA */}
      <section className="bg-navy text-white relative grain overflow-hidden">
        <div className="absolute top-0 left-1/3 w-96 h-96 bg-brand/15 rounded-full blur-[120px]" />
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-24 grid lg:grid-cols-2 gap-14 items-center relative">
          <Reveal>
            <img src={IMAGES.install} alt="Technicien CVC MS ÉNERGIE lors d'une installation et mise en service de climatisation dans le Maine-et-Loire" className="rounded-3xl w-full h-[420px] object-cover shadow-2xl border border-white/10" />
          </Reveal>
          <Reveal delay={0.15}>
            <Overline>Pourquoi MS Énergie</Overline>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-4">
              Un savoir-faire technique au service de votre confort
            </h2>
            <p className="text-white/60 mt-5 leading-relaxed">
              Particuliers et professionnels nous font confiance pour l'installation et l'entretien de leurs équipements. Chaque chantier est mené avec rigueur, des conseils honnêtes et un suivi attentif.
            </p>
            <div className="space-y-4 mt-8">
              {VALUES.map((v) => (
                <div key={v.title} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                    <v.icon className="w-5 h-5 text-brand" />
                  </div>
                  <div>
                    <p className="font-bold">{v.title}</p>
                    <p className="text-white/50 text-sm">{v.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* STEPS */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-24 md:py-28">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <Overline>Comment ça marche</Overline>
          <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-navy mt-4">
            Simple, rapide, transparent
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className="bg-surface border border-border rounded-2xl p-7 h-full">
                <span className="font-display text-5xl font-black text-brand/20">{s.n}</span>
                <h3 className="font-display font-bold text-lg text-navy mt-3">{s.title}</h3>
                <p className="text-text-muted text-sm mt-2 leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-surface border-y border-border py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <Overline>Avis clients</Overline>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-navy mt-4">
              Ils nous ont fait confiance
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.07}>
                <div className="bg-white border border-border rounded-2xl p-7 h-full flex flex-col" data-testid={`testimonial-${i}`}>
                  <Quote className="w-8 h-8 text-brand/30" />
                  <p className="text-navy/80 text-sm leading-relaxed mt-4 flex-1">"{t.text}"</p>
                  <div className="flex items-center gap-1 text-brand mt-5">
                    {[...Array(t.rating)].map((_, k) => <Star key={k} className="w-4 h-4 fill-current" />)}
                  </div>
                  <p className="font-bold text-navy mt-3">{t.name}</p>
                  <p className="text-text-muted text-xs">{t.city}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-24">
        <div className="bg-navy rounded-3xl p-10 md:p-16 text-center relative overflow-hidden grain">
          <div className="absolute -top-20 -right-20 w-80 h-80 bg-brand/20 rounded-full blur-[100px]" />
          <div className="relative">
            <h2 className="font-display text-3xl md:text-5xl font-black tracking-tighter text-white">
              Prêt à optimiser votre confort ?
            </h2>
            <p className="text-white/60 mt-5 max-w-xl mx-auto text-lg">
              Demandez votre devis gratuit ou prenez rendez-vous en ligne. Réponse rapide garantie.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-9">
              <Link to="/devis" className="bg-brand hover:bg-brand-dark text-white font-bold py-4 px-8 rounded-full transition-colors shadow-xl shadow-brand/30">
                Demander un devis
              </Link>
              <Link to="/rendez-vous" className="bg-white text-navy font-bold py-4 px-8 rounded-full hover:bg-white/90 transition-colors">
                Prendre rendez-vous
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
